import outs from "../../assets/conf/outs.json";

import { floor, isEqual, keyBy } from "lodash";
import { getConstantValue } from "@/shared/utils/constants";
import { Params, PoolDetails } from "@/models/pool-details.model";
import getSpeedCount from "@/shared/utils/misc/get-speed-count";
import { isNotNil } from "@/models/nullable.type";
import { Pool } from "@/models/pool.model";
import {
  deserializeRecurrence,
  serializeRecurrence,
} from "@/shared/utils/recurrence";
import { evaluate } from "mathjs";
import i18n from "@/core/i18n";
import { useApi } from "@/services/useApi.ts";

type Condition =
  | ["$filtrationSpeedCount", [string]]
  | [keyof Params | keyof Pool, string[]];

export type OutDef = {
  index: number;
  label: string;
  consoLabel: string;
  name: string;
  recModeFirstBit?: number;
  modes: {
    name?: string;
    label?: string;
    value: number;
    conditions: Condition[];
    minRoleToChange: number;
    quantities: {
      label: string;
      formula: string;
    }[];
    paramName?: keyof Params;
    planNames?: string[];
    paramRange?: [number, number];
    paramUnit?: string;
    paramPrecision?: number;
    hasRecurrence?: string;
    filtrRecurrence?: string;
    hasOnOff?: boolean;
    hasTrigger?: boolean;
    hasStop?: boolean;
    showStatus?: boolean;
    consigneName?: string;
    consigneRange?: [number, number];
    autoOffRange?: [number, number];
    forceState?: number;
  }[];
};

export const outsByIndex = keyBy(outs, (out) => out.index);
export const outsByName = keyBy(outs, (out) => out.name);

export const getOutByIndex = (index: string | number) =>
  outsByIndex[index] as unknown as OutDef;

export const getOutByName = (name: string) =>
  outsByName[name] as unknown as OutDef;

const evaluateSpeedCountCondition = (value: string, pool: Pool): boolean => {
  if (value === "1") {
    return getSpeedCount(pool) === 1;
  } else if (value === ">1") {
    return getSpeedCount(pool) > 1;
  } else {
    throw Error(
      `Unsupported value for $filtrationSpeedCount condition : ${value}`
    );
  }
};

const evaluateCondition = (
  [paramName, paramValues]: Condition,
  pool: Pool
): boolean => {
  if (paramName === "$filtrationSpeedCount") {
    return evaluateSpeedCountCondition(paramValues[0], pool);
  } else {
    return paramValues
      .map(getConstantValue)
      .includes(
        paramName in pool
          ? (pool[paramName as keyof Pool] as number)
          : pool.params[paramName as keyof Pool["params"]]
      );
  }
};

export const getAvailableModes = (index: string | number, pool: Pool) => {
  return (
    getOutByIndex(index)?.modes.filter(
      (mode) =>
        !mode.conditions ||
        mode.conditions.every((condition) => evaluateCondition(condition, pool))
    ) ?? []
  );
};

export const getActiveMode = (out: Pool["outs"][number], pool: Pool) => {
  return getAvailableModes(out.index, pool).find(
    (mode) => mode.value === out.mode
  );
};

export const formatDuration = (seconds: number): string => {
  if (seconds > 365 * 24 * 3600) {
    return `${floor(seconds / 3600 / 24 / 365)}${i18n.global.t(
      "YearSymbol"
    )} ${formatDuration(seconds % (365 * 24 * 3600))}`;
  }
  if (seconds > 24 * 3600) {
    return `${floor(seconds / 3600 / 24)}${i18n.global.t(
      "DaySymbol"
    )} ${formatDuration(seconds % (24 * 3600))}`;
  }
  if (seconds > 3600) {
    return `${floor(seconds / 3600)}h ${floor((seconds % 3600) / 60)}mn`;
  } else {
    return `${(seconds / 60).toFixed(0)}mn`;
  }
};

export const formatAmount = (amount: number, label: string): string => {
  if (label.startsWith("Inject")) {
    if (label === "InjectToday" || amount < 1000) {
      return `${Math.round(amount)}mL`;
    } else {
      return `${Math.round(amount / 100) / 10}L`;
    }
  } else if (label.startsWith("Duree")) {
    return formatDuration(amount);
  } else if (label.startsWith("Gram")) {
    return `${Math.round(amount)}g`;
  } else {
    return `${Math.round(amount)}`;
  }
};

export const computeQuantities = (
  out: PoolDetails["outs"][number],
  pool: Pool,
  quantityFormatter: (quantity: string) => string = (quantity) => quantity
): string[] => {
  const { t } = i18n.global;
  const mode = getActiveMode(out, pool);
  if (mode?.quantities) {
    return mode.quantities
      .map(({ formula, label }) => {
        try {
          return t(label, {
            quantity: quantityFormatter(
              formatAmount(
                evaluate(formula, {
                  Out: { todayTime: 0, totalTime: 0, ...out },
                  ...pool.params,
                  ...pool.ExtraParams,
                }),
                label
              )
            ),
          });
        } catch (e) {
          return null;
        }
      })
      .filter(isNotNil);
  } else {
    return [];
  }
};

export const deserializeOut = (out: PoolDetails["outs"][number]) => {
  return {
    ...out,
    newOffDelay: out.offDelay,
    recurrence: deserializeRecurrence(out.recurMode, out.recurDate),
  };
};

export const getNewState = (out: Pool["outs"][number], pool: Pool): number => {
  const mode = getActiveMode(out, pool);
  if (isNotNil(mode?.forceState)) {
    return mode!.forceState;
  }

  if (out.mode === 0 || out.mode === 6) {
    return out.status;
  } else {
    return 2;
  }
};

export const useSaveOut = () => {
  const api = useApi();

  return (
    out: Pool["outs"][number],
    oldOut: Pool["outs"][number],
    pool: Pool,
    forceOutState: boolean
  ): Promise<unknown> => {
    /* Il faut envoyer la commande "SetOut" en dernier (après le SetAutoOff et/ou le SetParam).
     C'est important dans le cas ou le "newsate" est 1 car cela déclancherai la minuterie avec la valeur précédente */

    const saveOffDelay =
      isNotNil(out.newOffDelay) && out.newOffDelay !== out.offDelay
        ? api
            .setAutoOff({
              poolID: pool!.idSystem,
              outIdx: out.index,
              offDelay: out.newOffDelay,
              comMode: 1,
            })
            .then(api.waitCommand)
        : Promise.resolve();

    return saveOffDelay.then(() =>
      api
        .setOut({
          poolID: pool!.idSystem,
          outIdx: out.index,
          newMode: out.mode,
          newState: forceOutState ? out.status : getNewState(out, pool),
          comMode: 1,
          ...buildRecurrenceFlags(out, oldOut),
        })
        .then(api.waitCommand)
    );
  };
};

export const buildRecurrenceFlags = (
  out: Pool["outs"][number],
  oldOut: Pool["outs"][number]
): { custFlags?: number; otherFlags?: number } => {
  console.log(out.recurrence, oldOut.recurrence);
  if (
    !out.withSyncColor &&
    !out.withChangeColor &&
    (!out.recurrence ||
      out.recurrence.mode === "-1" ||
      isEqual(out.recurrence, oldOut?.recurrence))
  ) {
    return {};
  }

  const outDef = getOutByIndex(out.index);
  const recModeFirstBit = outDef.recModeFirstBit;

  if (!isNotNil(recModeFirstBit)) {
    throw new Error(
      `Missing recModeFirstBit configuration for out index ${out.index}`
    );
  }

  const flags = serializeRecurrence(
    out.recurrence!,
    out.flags,
    recModeFirstBit
  );

  if (out.withChangeColor) {
    flags.custFlags = flags.custFlags | (1 << 3);
  }
  if (out.withSyncColor) {
    flags.custFlags = flags.custFlags | (1 << 10);
  }

  // https://trello.com/c/HaTQy3A3/152-v%C3%A9rifier-les-conditions-dans-lesquelles-on-envoie-les-param%C3%A8tres-custflags-et-otherflags-pour-setout
  // Il ne faut jamais envoyer “otherFlags” pour les sorties “Filtration” et chauffage” car leur définition est différente.
  if (
    getOutByIndex(out.index).name === "OUT_CHAUF" ||
    getOutByIndex(out.index).name === "OUT_FILTRE"
  ) {
    return { custFlags: flags.custFlags };
  }

  return flags;
};

export const shouldHideOut = (
  out: Pool["outs"][number],
  pool: Pool
): boolean => {
  // Hardcoded specific edge case that's not part of the conf

  // https://trello.com/c/rcXe7U5Z/163-les-bassins-en-mode-chlore-hybride-apparaissent-avec-le-mode-inconnu
  if (out.index === 15 && pool.HybrideMode === 1) {
    return true;
  }

  return false;
};

export const isOutHiddenForHivernage = (index: string | number) => {
  const typeName = getOutByIndex(index).name;

  return ["OUT_PH", "OUT_TRAIT", "OUT_TRAIT2", "OUT_FLOC"].includes(typeName);
};

export const getOutIcon = (outName: string) => {
  if (outName === "OUT_LIGHT") {
    return "eclairage";
  } else if (outName === "OUT_FILTRE") {
    return "filtration";
  } else if (outName === "OUT_CHAUF") {
    return "chauffage";
  } else if (outName.startsWith("OUT_AUX")) {
    return `sortie-auxiliaire-0${outName.charAt(outName.length - 1)}`;
  }
};
